  // src/components/GoogleTranslate/TranslationContext.js

  import React, { createContext, useState, useEffect, useCallback } from 'react';
  import PropTypes from 'prop-types';
  import languageConfiguration from "./options.json";
  
  
    // Initialize the TranslationContext with default values
  export const TranslationContext = createContext({
    currentLanguage  : 'en',
    isReady          : false,
    translationActive: false,
    setLanguage      : () => {},
    toggleTranslation: () => {},
  });
  
    // Create a Provider component
  export const TranslationProvider = ({ children, language = 'en' }) =>
  {   const [currentLanguage,   setCurrentLanguage  ] = useState(language);
      const [isReady,           setIsReady          ] = useState(false);
      const [translationActive, setTranslationActive] = useState(language !== 'en');
  
      // Function to change the language
      const changeLanguage = useCallback((language) =>
      {   setCurrentLanguage(language);
          setTranslationActive(language !== 'en');
  
      // Trigger Google Translate widget to change language
          if (window.googleTranslate?.isScriptLoaded)
          {   const combo = document.querySelector('.goog-te-combo');
              if (combo)
              {   combo.value = language;
                  combo.dispatchEvent(new Event('change', { bubbles: true }));
              }
          }
      },
      []);
  
  
  
      const activeLanguages = languageConfiguration.languages
          .filter(lang => lang.enabled)
          .map(lang => lang.code)
          .join(',');
      
  
      // Function to toggle translation active state
      const toggleTranslation = useCallback(() =>
      {   setTranslationActive((prev) => !prev);
          // Optionally, toggle translation by switching language to 'en' or currentLanguage
          if (window.googleTranslate?.isScriptLoaded)
          {   const combo = document.querySelector('.goog-te-combo');
              if (combo)
              {   const newLang     = translationActive ? 'en' : currentLanguage;
                  combo.value       = newLang;
                  combo.dispatchEvent(new Event('change', { bubbles: true }));
              }
          }
      }, [translationActive, currentLanguage]);
  
      useEffect(() =>
      {   if (typeof window === 'undefined') return;
  
          const loadGoogleTranslate = () =>
          {   if (window.googleTranslate?.isScriptLoaded)
              {   setIsReady(true);
                  return;
              }
  
              window.googleTranslate = window.googleTranslate ||
              {   isScriptLoaded  : false,
                  portalNode      : null,
                  translationCache: 
                  {   currentLanguage: 'en',
                      translations   : new Map(),
                      lastTranslated : null
                  },
              scriptLoadPromise: null,
              translateAPI     : null
              };
  
              window.googleTranslate.scriptLoadPromise = new Promise((resolve) =>
              {   window.googleTranslateElementInit = () =>
                  {   try
                      {   // Wrap initialization in try-catch
                          const element               = document.createElement('div');
                                  element.style.display = 'none';
                                  element.id            = 'google-translate-element';
                          document.body.appendChild(element);
                  
                  // Add delay to ensure google.translate is fully loaded
                          setTimeout(() =>
                          {   try
                              {   const translateElement = new window.google.translate.TranslateElement(
                                      {   pageLanguage: 'en',
                                          includedLanguages: activeLanguages ? activeLanguages : 'es,de,fr,zh-CN,ja,hi,ar',
                                          autoDisplay: false
                                      }, element.id);
  
                                      window.googleTranslate.translateAPI =
                                      {   translate: async (text, targetLang) =>
                                          {   return new Promise((resolve, reject) =>
                                              {   try
                                                  {   const sourceText = text.trim();
                                                      if (!sourceText) return resolve('');
  
                                                      // Check cache first
                                                      const cache             = window.googleTranslate.translationCache.translations;
                                                      const cacheKey          = `${targetLang}:${sourceText}`;
                                                      const cachedTranslation = cache.get(cacheKey);
                              
                                                      if (cachedTranslation)
                                                      {   console.log(`Using cached translation for: ${sourceText}`);
                                                          return resolve(cachedTranslation.text);
                                                      }
  
                                                      // Create a temporary container
                                                      const container               = document.createElement('div');
                                                              container.style.display = 'none';
                                                              container.textContent   = sourceText;
                                                      document.body.appendChild(container);
  
                                                      // Use Google's internal translation method
                                                      const observer = new MutationObserver((mutations) =>
                                                      {   try
                                                          {   for (const mutation of mutations)
                                                              {   if
                                                                  (   mutation.target       === container &&
                                                                      mutation.type         === 'childList' &&
                                                                      container.textContent !== sourceText
                                                                  )
                                                                  {   const translatedText = container.textContent;
                                                                      // Cache the result
                                                                      cache.set(cacheKey,
                                                                      {   text: translatedText,
                                                                          timestamp: Date.now()
                                                                      });
                                                          
                                                                      observer.disconnect();
                                                                      container.remove();
                                                                      resolve(translatedText);
                                                                      return;
                                                                  }
                                                              }
                                                          }
                                                          catch (err)
                                                          {   console.warn('Translation observer error:', err);
                                                              observer.disconnect();
                                                              container.remove();
                                                              resolve(sourceText); // Fallback to original text
                                                          }
                                                      });
  
                                                      observer.observe(container,
                                                      {   childList    : true,
                                                          characterData: true,
                                                          subtree      : true
                                                      });
  
                                                      // Trigger translation with retry
                                                      const triggerTranslation = (retryCount = 0) =>
                                                      {   try
                                                          {   const combo = document.querySelector('.goog-te-combo');
                                                              if (combo)
                                                              {   combo.value = targetLang;
                                                                  combo.dispatchEvent(new Event('change'));
                                                              }
                                                              else if (retryCount < 3)
                                                              {   // Retry up to 3 times with increasing delay
                                                                  setTimeout(() => triggerTranslation(retryCount + 1),
                                                                                              1000 * (retryCount + 1));
                                                              }
                                                              else
                                                              {   observer.disconnect();
                                                                  container.remove();
                                                                  resolve(sourceText);
                                                              }
                                                          }
                                                          catch (err)
                                                          {   console.warn('Translation trigger error:', err);
                                                              if (retryCount < 3)
                                                                  setTimeout(() => triggerTranslation(retryCount + 1),
                                                                                              1000 * (retryCount + 1)
                                                                              );
                                                              else
                                                              {   observer.disconnect();
                                                                  container.remove();
                                                                  resolve(sourceText);
                                                              }
                                                          }
                                                      };
  
                                                      triggerTranslation();
  
                                                      // Timeout after 5 seconds
                                                      setTimeout(() =>
                                                      {   observer.disconnect();
                                                          container.remove();
                                                          resolve(sourceText);
                                                      }, 5000);
                                                  }
                                                  catch (err)
                                                  {   console.warn('Translation setup error:', err);
                                                      resolve(text.trim());
                                                  }
                                              });
                                          }
                                      };
  
                                  window.googleTranslate.isScriptLoaded = true;
                                  setIsReady(true);
                                  resolve();
                              }
                              catch (err)
                              {   console.warn('Google Translate initialization error:', err);
                                  // Still mark as loaded to prevent infinite retries
                                  window.googleTranslate.isScriptLoaded = true;
                                  setIsReady(true);
                                  resolve();
                              }
                          }, 500); // Add small delay for script initialization
                      }
                      catch (err)
                      {   console.warn('Google Translate element creation error:', err);
                          resolve();
                      }
                  };
  
                  // Add error handling for script loading
                  const script       = document.createElement('script');
                  script.src         = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
                  script.async       = true;
              script.onerror       = () =>
                  {   console.warn('Failed to load Google Translate script');
                      resolve();
                  };
              document.body.appendChild(script);
              });
          };
  
          loadGoogleTranslate();
  
          return () =>
          {   try
                  { delete window.googleTranslateElementInit; }
              catch (err)
                  { console.warn('Cleanup error:', err); }
          };
      }, []);
  
      // Effect to trigger translation when language changes
      useEffect(() =>
      {   if (isReady && translationActive && currentLanguage !== 'en')
          { const combo = document.querySelector('.goog-te-combo');
              if (combo)
              {   combo.value = currentLanguage;
                  combo.dispatchEvent(new Event('change', { bubbles: true }));
              }
          }
      }, [currentLanguage, translationActive, isReady]);
  
      return (
      <TranslationContext.Provider value={{
          currentLanguage,
          isReady,
          translationActive,
          setLanguage: changeLanguage,
          toggleTranslation,
      }}>
          {children}
      </TranslationContext.Provider>
      );
  };
  
  TranslationProvider.propTypes =
  {   children: PropTypes.node.isRequired,
      language: PropTypes.string,
  };
  
  TranslationProvider.defaultProps =
  { language: 'en' };
  